import axios from 'axios';

import { scenarioConstants, businessConstants } from '../constants';
import { loadCurrenciesForScenario } from './currencyActions';
import { toMap } from '../../utils/scenarioTransforms';
import { entityServices } from '../services/entityServices';
import { pushUndo } from '../utils/undoHistory';

const findEntityById = (list, id) => {
    if (!Array.isArray(list)) return null;
    for (const ent of list) {
        if (Number(ent.entity_id) === Number(id)) return ent;
        const found = findEntityById(ent.children || [], id);
        if (found) return found;
    }
    return null;
};


export const scenarioActions = {
    getScenario,
    getScenariosList,
    createValues,
    compileCurrentScenario,
    calculateConvergence,
    openScenarioAndConverge,
    updateValue,
    updateValuesPeriods,
    updateRollUpEffect,
    createChangeInValues,
    createScenario,   
    updateScenario,   
    deleteScenario, 
    reorderScenarios,
};

const scenarioControllers = new Map();

// ─── CREATE SCENARIO ─────────────────────────────────────────────────────────

function createScenario(parentEntityId, name, scenarioTemplateId, localCurrencyId) {
    return async (dispatch, getState) => {
        dispatch({ type: scenarioConstants.CREATE_SCENARIO_REQUEST });

        try {
             const parsedEntityId = Number(parentEntityId);
            if (!Number.isFinite(parsedEntityId) || parsedEntityId <= 0) {
                console.error('[createScenario] Invalid parentEntityId', parentEntityId);
                dispatch({
                    type:  scenarioConstants.CREATE_SCENARIO_FAILURE,
                    error: 'Invalid entity — please select a valid unit before creating a scenario.',
                });
                return;
            }
            const organization    = getState().organization.organization;
            const entityStructure = [...getState().entities.entities];
            const parentEntity    = parentEntityId == null
                ? null
                : findEntityById(entityStructure, parentEntityId);

            // Minimal payload — backend loads accountStructure/periods/expandedPeriods itself
            const payload = {
                entityId:         parsedEntityId,
                name:              name || 'New Scenario',
                scenarioTemplateId,
                key:               entityServices.createNewKey(parentEntity, entityStructure),
                localCurrencyId:   localCurrencyId ?? organization.base_currency,
                groupCurrencyId:   organization.base_currency,
            };

            const response = await axios.post('/scenarios', payload);

            if (response.status === 201) {
                const newScenario = {
                    scenario_id:       response.data.scenario_id,
                    entity_id:         response.data.entity_id,
                    name:              response.data.name,
                    key:               `s:${response.data.scenario_id}`, // deterministic
                    scenario_currency: response.data.scenario_currency,
                    group_currency:    response.data.group_currency,
                };

                entityServices.insertNewScenario(newScenario, entityStructure);

                // Single dispatch — one re-render
                dispatch({
                    type:           scenarioConstants.CREATE_SCENARIO_SUCCESS,
                    parsedScenario: response.data,
                    entities:       entityStructure,
                });

                const freshScenario = await dispatch(
                    getScenario(response.data.scenario_id, {
                        force: true,
                    }),
                );

                const state = getState();

                if (freshScenario) {
                    await dispatch(
                        calculateConvergence(
                            freshScenario,
                            state.accounts.accountStructure,
                            state.allocations?.allocations ?? [],
                        ),
                    );
                }


                return response.data;
            }

            if (response.status === 500) {
                dispatch({
                    type:  scenarioConstants.CREATE_SCENARIO_FAILURE,
                    error: response.data.toString(),
                });
            }

        } catch (error) {
            dispatch({
                type:  scenarioConstants.CREATE_SCENARIO_FAILURE,
                error: error.response
                    ? error.response.data.toString()
                    : 'Unable to create scenario at this moment. Please try again later.',
            });
        }
    };
}

// ─── UPDATE SCENARIO ─────────────────────────────────────────────────────────

function updateScenario(id, scenarioUpdates) {
    return async (dispatch) => {
        dispatch({ type: scenarioConstants.UPDATE_SCENARIO_REQUEST });

        try {
            const response = await axios.put(`/scenarios/${id}`, { scenarioUpdates });

            if (response.status === 200) {
                dispatch({
                    type:           scenarioConstants.UPDATE_SCENARIO_SUCCESS,
                    parsedScenario: response.data.parsedScenario,
                    entities:       response.data.entities,
                });
            }

            if (response.status === 500) {
                dispatch({
                    type:  scenarioConstants.UPDATE_SCENARIO_FAILURE,
                    error: response.data.toString(),
                });
            }

        } catch (error) {
            dispatch({
                type:  scenarioConstants.UPDATE_SCENARIO_FAILURE,
                error: error.response
                    ? error.response.data.toString()
                    : 'Unable to update the scenario at this moment. Please try again later.',
            });
        }
    };
}

// ─── DELETE SCENARIO ─────────────────────────────────────────────────────────

function deleteScenario(scenario) {
    return async (dispatch, getState) => {
        const scenarioIdToDelete = Number(scenario?.scenario_id);
        if (!scenarioIdToDelete) {
            console.error('[deleteScenario] Invalid scenario_id', scenario);
            return;
        }

        dispatch({
            type:       scenarioConstants.DELETE_SCENARIO_REQUEST,
            scenarioId: scenarioIdToDelete,
        });

        let confirmedScenarioId;
        let confirmedEntityId;

        try {
            const response = await axios.delete('/scenarios', {
                data: { id: scenarioIdToDelete },
            });

            if (response.status !== 200) {
                dispatch({
                    type:  scenarioConstants.DELETE_SCENARIO_FAILURE,
                    error: response.data?.toString?.() || 'Delete failed',
                });
                return;
            }

            confirmedScenarioId = Number(response.data?.scenario_id);
            confirmedEntityId   = Number(response.data?.entity_id);

            if (!confirmedScenarioId) {
                console.warn('[deleteScenario] Backend did not return scenario_id, falling back.', response.data);
                confirmedScenarioId = scenarioIdToDelete;
            }
            if (!confirmedEntityId) {
                console.warn('[deleteScenario] Backend did not return entity_id, falling back.', response.data);
                confirmedEntityId = Number(scenario.entity_id);
            }

            dispatch({
                type:       scenarioConstants.DELETE_SCENARIO_SUCCESS,
                entityId:   confirmedEntityId,
                scenarioId: confirmedScenarioId,
            });

            const current = getState().scenarios.currentScenario;
            if (Number(current?.scenario_id) === confirmedScenarioId) {
                dispatch({ type: scenarioConstants.SET_CURRENT_SCENARIO_NULL_SUCCESS });
            }

        } catch (error) {
            dispatch({
                type:  scenarioConstants.DELETE_SCENARIO_FAILURE,
                error: error.response
                    ? error.response.data?.toString?.()
                    : 'Unable to delete scenario.',
            });
            throw error;
        }

        // Best-effort BC cleanup
        try {
            const bcState = getState().business.businessCases ?? [];

            let newBusinessCaseStructure = bcState.map((bc) => ({
                ...bc,
                business_scenario: (bc.business_scenario ?? []).filter(
                    (s) => Number(s.scenario_id) !== confirmedScenarioId
                ),
            }));

            const empties = newBusinessCaseStructure.filter(
                (bc) => (bc.business_scenario?.length ?? 0) === 0
            );

            const results = await Promise.allSettled(
                empties.map((bc) =>
                    axios
                        .delete('/business-cases', { data: { id: bc.business_case_id } })
                        .then(() => bc.business_case_id)
                )
            );

            const deletedIds = new Set(
                results
                    .filter((r) => r.status === 'fulfilled')
                    .map((r) => r.value)
            );

            newBusinessCaseStructure = newBusinessCaseStructure.filter((bc) => {
                const isEmpty = (bc.business_scenario?.length ?? 0) === 0;
                return isEmpty ? deletedIds.has(bc.business_case_id) : true;
            });

            dispatch({
                type:                   businessConstants.DELETE_SCENARIO_FROM_BUSINESS_CASES,
                businessCaseStructure:  newBusinessCaseStructure,
            });

            results
                .filter((r) => r.status === 'rejected')
                .forEach((r) => console.warn('[deleteScenario] BC cleanup failed', r.reason));

        } catch {
            // swallow cleanup errors
        }
    };
}


function getScenario(scenario, { force = false } = {}) {
  return async (dispatch, getState) => {
    if (scenario == null) return null;

    const id = typeof scenario === 'number' ? scenario : scenario.scenario_id;
    if (id == null) return null;

    const strId = String(id);
    const { deletingScenarioIds } = getState().scenarios;

    if (deletingScenarioIds?.has(strId)) return null;
    if (!force && scenarioControllers.has(strId)) return null;

    if (force) scenarioControllers.get(strId)?.abort();

    const controller = new AbortController();
    scenarioControllers.set(strId, controller);

    dispatch({ type: scenarioConstants.GET_SCENARIO_REQUEST, id });

    try {
      const res = await axios.get(`/scenarios/${id}`, { signal: controller.signal });
      scenarioControllers.delete(strId);

      dispatch({ type: scenarioConstants.GET_SCENARIO_SUCCESS, parsedScenario: res.data });
      return {
        compiled: false,
        scenario_id: res.data.scenario_id,
        originalValues: toMap(res.data.originalValues),
        values: toMap(res.data.values),
        roll_up: toMap(res.data.roll_up),
        scenario_currency: res.data.scenario_currency,
        group_currency: res.data.group_currency,
      };
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return null;
      scenarioControllers.delete(strId);

      const status = error.response?.status;
      if (status === 404 || status === 410) {
        dispatch({ type: scenarioConstants.SET_CURRENT_SCENARIO_NULL_SUCCESS });
        return null;
      }

      dispatch({
        type: scenarioConstants.GET_SCENARIO_FAILURE,
        error: error.response?.data?.toString?.() || 'Unable to fetch the scenario.',
      });
      throw error;
    } finally {
      scenarioControllers.delete(strId);
    }
  };
}

function openScenarioAndConverge(scenario, accountStructure, allocations, editedCells = []) {
    return async (dispatch, getState) => {
        dispatch({ type: scenarioConstants.OPEN_SCENARIO_REQUEST });

        try {
            await dispatch(getScenario(scenario, { force: true }));

            const loadedScenario = getState().scenarios.currentScenario;

            if (!loadedScenario) {
                throw new Error('Scenario was not loaded.');
            }

            await dispatch(
                calculateConvergence(
                    loadedScenario,
                    accountStructure,
                    allocations,
                    editedCells
                )
            );

            dispatch({ type: scenarioConstants.OPEN_SCENARIO_SUCCESS });
        } catch (error) {
            dispatch({
                type: scenarioConstants.OPEN_SCENARIO_FAILURE,
                error: error.message || 'Unable to open scenario.',
            });
        }
    };
}

function getScenariosList(options = {}) {
  return async (dispatch, getState) => {
    // optional cache: don't refetch if already loaded
    if (!options.force && getState().scenarios?.scenariosList) return getState().scenarios.scenariosList;

    dispatch({ type: scenarioConstants.GET_SCENARIOS_LIST_REQUEST });

    try {
      const res = await axios.get('/scenarios');
      dispatch({
        type: scenarioConstants.GET_SCENARIOS_LIST_SUCCESS,
        scenariosList: res.data,
      });
      return res.data;
    } catch (error) {
      dispatch({
        type: scenarioConstants.GET_SCENARIOS_LIST_FAILURE,
        error: error.response?.data?.toString?.() || 'Unable to fetch scenarios list.',
      });
      throw error;
    }
  };
}
function createValues(newAccount, newValues) {
    return async (dispatch, getState) => {
        try {
            const scenarios = getState().scenarios.scenarios;
            
            if (scenarios.size > 0) {
                newValues.forEach((value) => {
                    const scenarioKey = String(value.scenario_id);
                    if (scenarios.has(value.scenario_id)) {
                        const scenario = { ...scenarios.get(scenarioKey) };

                        scenario.originalValues = new Map(scenario.originalValues);
                        scenario.values = new Map(scenario.values);
                        scenario.roll_up = new Map(scenario.roll_up);
               
                        scenario.originalValues.set(newAccount.account_number, value.value);
                        scenario.values.set(newAccount.account_number, value.value);
                        scenario.roll_up.set(newAccount.account_number, '');

                        scenario.compiled = false;

                       scenarios.set(scenarioKey, scenario);
                    }
                });

                 dispatch(success(scenarios));
                return scenarios;
            }
        } catch (error) {
            console.error(error);
            const msg = error.response
                ? error.response.data.toString()
                : 'Unable to update account structure at this moment. Please try again later.';

            dispatch(failure(msg));
            throw error; 
    }
  };

    function success(newScenarios) {
        return { type: scenarioConstants.CREATE_VALUES_SUCCESS, newScenarios };
    }
    function failure(error) {
        return { type: scenarioConstants.CREATE_VALUES_FAILURE, error };
    }
}

function compileCurrentScenario() {
    return async (dispatch) => {
        try {
            dispatch(success());
        } catch (error) {
            console.error(error);
            if (error.response) {
                dispatch(failure(error.response.data.toString()));
            } else {
                dispatch(failure('Unable to update account structure at this moment. Please try again later.'));
            }
        }
    };

    function success() {
        return { type: scenarioConstants.COMPILE_CURRENT_SCENARIO_SUCCESS };
    }
    function failure(error) {
        return {
            type: scenarioConstants.COMPILE_CURRENT_SCENARIO_FAILURE,
            error,
        };
    }
}

// Latest converge request per scenario. A response to an older request (e.g. two
// formula saves in a row) can arrive last, and would replace the newer result.
const latestConvergeRequest = new Map();

function calculateConvergence(currentScenario, accountStructure, allocations, editedCells = [], viewCurrencyId = null) {
    return async (dispatch, getState) => {
        const requestId = {};
        latestConvergeRequest.set(currentScenario?.scenario_id, requestId);
        const isStale = () => latestConvergeRequest.get(currentScenario?.scenario_id) !== requestId;

        dispatch(request());

        try {
            const organization = getState().organization.organization;
            const sid = currentScenario?.scenario_id;
            const scenarioCurrencyId = viewCurrencyId ?? currentScenario.scenario_currency;

            // Ensure currencies are loaded
            let currencies = getState().currency.currency;
            if (!Array.isArray(currencies) || currencies.length === 0) {
                if (sid) {

                // try to hydrate currencies for this scenario
                await dispatch(loadCurrenciesForScenario(sid));
                currencies = getState().currency.currency;
                }

                if (!Array.isArray(currencies) || currencies.length === 0) {
                    
                // avoid crashing backend
                return dispatch(
                    failure("Currencies not loaded yet. Please try again.")
                );
                }
            }
           
            const url = '/converge';       

            // 1 Convert editedCells → backend format
            const edits = (editedCells || []).map(c => ({
                
                value_id: c.value_id,
                value: String(c.value),
            }));
                   
            // No account structure: the server loads it itself, and on a large
            // model it is several MB to upload and parse on every converge.
            const response = await axios.post(url, {
                currentScenarioId: currentScenario.scenario_id,
                originalValues: Object.fromEntries(currentScenario.originalValues),
                rollUp: Object.fromEntries(currentScenario.roll_up),
                currencies,
                baseCurrencyId: organization.base_currency,
                scenarioCurrencyId: viewCurrencyId ?? currentScenario.scenario_currency,
                inputCurrencyId: currentScenario.scenario_currency,
                prevScenarioCurrencyId: currentScenario.prev_scenario_currency,
                expandedPeriods: organization.expanded_periods,
                allocations: allocations,
                edits,
            });

            // A newer converge of this scenario is under way; its result wins.
            if (isStale()) return;

            if (response.status === 200) {
                const compilation = response.data;

                let newScenario = { ...currentScenario };
                newScenario.compiled = compilation.compiled;
                newScenario.values = new Map(Object.entries(compilation.values));
                compilation.viewCurrencyId = scenarioCurrencyId;
                compilation.compiledScenario = newScenario;
                dispatch(success(compilation));
            }
            if (response.status === 500) dispatch(failure(response.data.toString()));
        } catch (error) {
            if (isStale()) return;
            if (error.response) {
                dispatch(failure(error.response.data.toString()));
            } else {
                dispatch(failure('Unable to update scenario at this moment. Please try again later.'));
            }
        }
    };

    function request() {
        return { type: scenarioConstants.CALCULATE_CONVERGENCE_REQUEST };
    }
    function success(compilation) {
        return {
            type: scenarioConstants.CALCULATE_CONVERGENCE_SUCCESS,
            compilation,
        };
    }
    function failure(error) {
        return { type: scenarioConstants.CALCULATE_CONVERGENCE_FAILURE, error };
    }
}

function updateValue(account, value, period, currentScenario, viewCurrencyId) {
    return async (dispatch) => {
        dispatch(request());

        try {
            const accountNumber =
                account['account'] === undefined ? account.account_number : account.account.account_number;
                        
            const values = currentScenario.values.get(accountNumber);
            const currentValue = values.filter((value) => value.period === period.slice(6))[0];
            const url = '/value/' + currentValue.value_id;

            const response = await axios.put(url, {
                value: value,
                viewCurrencyId,
            });
            const { undo, ...updatedValue } = response.data;

            if (response.status === 200) {
                dispatch(success({ account: account, updatedValue: updatedValue }));
                dispatch(pushUndo(`${accountNumber} · ${period.slice(6)}: ${value}`, undo));
            }
            if (response.status === 500) dispatch(failure(response.data.toString()));
        } catch (error) {
            if (error.response) {
                dispatch(failure(error.response.data.toString()));
            } else {
                dispatch(failure('Unable to update value at this moment. Please try again later.'));
            }
        }
    };

    function request() {
        return { type: scenarioConstants.UPDATE_VALUE_REQUEST };
    }
    function success(updatesValues) {
        return { type: scenarioConstants.UPDATE_VALUE_SUCCESS, updatesValues };
    }
    function failure(error) {
        return { type: scenarioConstants.UPDATE_VALUE_FAILURE, error };
    }
}

function updateValuesPeriods(organization, expandedPeriods, value, type) {
    return async (dispatch) => {
        try {
            await axios.put('/value', {
                organization: organization,
                expandedPeriods: expandedPeriods,
                value: value,
                type: type,
            });
        } catch (error) {
            if (error.response) {
                dispatch(failure(error.response.data.toString()));
            } else {
                dispatch(failure('Unable to update scenario at this moment. Please try again later.'));
            }
        }
    };

    function failure(error) {
        return { type: scenarioConstants.UPDATE_VALUES_PERIODS_FAILURE, error };
    }
}

function updateRollUpEffect(scenario, account, accountStructure, organization) {
    return async (dispatch) => {
        dispatch(request());

        try {
            let newScenario = { ...scenario };
            let newRollUps = new Map(scenario.roll_up);
            let oldRollUp = [];
            scenario.roll_up.get(account.account_id).map((roll) => oldRollUp.push(JSON.parse(JSON.stringify(roll))));

            let values_id = oldRollUp[0];
            let newRollUp = '';

            if (oldRollUp[1] === '') {
                newRollUp = 'S';
            } else if (oldRollUp[1] === 'S') {
                newRollUp = 'I';
            } else {
                newRollUp = '';
            }

            const response = await axios.put('/values/roll_up/' + values_id, {
                newRollUp: newRollUp,
            });

            if (response.status === 200) {
                newRollUps.set(account.account_id, [values_id, response.data.roll_up]);
                newScenario.roll_up = newRollUps;

                dispatch(success(newScenario));
            }
            if (response.status === 500) dispatch(failure(response.data.toString()));
        } catch (error) {
            if (error.response) {
                dispatch(failure(error.response.data.toString()));
            } else {
                dispatch(failure('Unable to update effect at this moment. Please try again later.'));
            }
        }
    };

    function request() {
        return { type: scenarioConstants.UPDATE_EFFECT_REQUEST };
    }
    function success(scenario) {
        return { type: scenarioConstants.UPDATE_EFFECT_SUCCESS, scenario };
    }
    function failure(error) {
        return { type: scenarioConstants.UPDATE_EFFECT_FAILURE, error };
    }
}

function createChangeInValues(response) {
    return async (dispatch) => {
        try {
            const newScenarios = new Map();
            for (const scenarioId in response.data.newScenarios) {
                newScenarios.set(+scenarioId, {
                    compiled: false,
                    scenario_id: response.data.newScenarios[scenarioId].scenario_id,
                    originalValues: new Map(Object.entries(response.data.newScenarios[scenarioId].originalValues)),
                    values: new Map(Object.entries(response.data.newScenarios[scenarioId].values)),
                    roll_up: new Map(Object.entries(response.data.newScenarios[scenarioId].roll_up)),
                });
            }

            if (response.status === 201) dispatch(success(newScenarios));
            if (response.status === 500) dispatch(failure(response.data.toString()));
        } catch (error) {
            if (error.response) {
                dispatch(failure(error.response.data.toString()));
            } else {
                dispatch(failure('Unable to create the change in values at this moment. Please try again later.'));
            }
        }
    };

    function success(scenarios) {
        return {
            type: scenarioConstants.CREATE_CHANGE_IN_VALUES_SUCCESS,
            scenarios,
        };
    }
    function failure(error) {
        return {
            type: scenarioConstants.CREATE_CHANGE_IN_VALUES_FAILURE,
            error,
        };
    }
}

function reorderScenarios (entityId, orderedIds) {
  return async (dispatch, getState) => {
    const snapshot = getState().entities.entities;
    const ids = orderedIds.map(Number);
    const eid = Number(entityId);

  dispatch({ type: scenarioConstants.REORDER_SCENARIOS_REQUEST, entityId: eid, orderedIds: ids });

    try {
      const { data } = await axios.put('/scenarios/reorder', { entity_id: eid, ordered_ids: ids });
      dispatch({ type: scenarioConstants.REORDER_SCENARIOS_SUCCESS, entities: data.entities });
      return data.entities;
    } catch (err) {
      dispatch({ type: scenarioConstants.REORDER_SCENARIOS_FAILURE, entities: snapshot, error: err });
      throw err;
    }
  };
}